import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Calendar as CalendarIcon, MapPin, Clock, Users, DollarSign,
  Trophy, PlusCircle, ChevronLeft, ChevronRight, Check, User, Pencil,
  Sparkles, Flame, Star, ArrowRight, ExternalLink, Share2, UserMinus,
  Award, Medal, Target, Plus, X, Camera, Wand2
} from 'lucide-react';
import ScorecardScanner from '../components/ScorecardScanner';
import { toast } from 'sonner';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EVENT_FILTERS = [
  { id: 'all', label: 'All Events', icon: Sparkles },
  { id: 'tee_time', label: 'Tee Times', icon: Clock },
  { id: 'tournament', label: 'Tournaments', icon: Trophy },
  { id: 'weekly_game', label: 'Weekly', icon: Flame },
  { id: 'social', label: 'Social', icon: Users },
  { id: 'casual_round', label: 'Casual', icon: Star },
];

export default function Events() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('upcoming'); // 'upcoming' | 'past'
  const [postingResults, setPostingResults] = useState(false); // event being edited
  const deepLinkProcessedRef = React.useRef(false);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, view]);

  // Deep-link: open event modal once when ?event=<id> is in URL and events load
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    const eventIdParam = searchParams.get('event');
    if (eventIdParam && events.length > 0) {
      const match = events.find(e => e.event_id === eventIdParam);
      if (match) {
        deepLinkProcessedRef.current = true;
        selectEvent(match);
      }
    }
  }, [events, searchParams]);

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const todayStr = new Date().toISOString().slice(0, 10);
      const qs = view === 'past' ? 'past=true&upcoming=false' : 'upcoming=true';
      const [evtRes, trRes] = await Promise.all([
        fetch(`${API}/events?${qs}`, { credentials: 'include' }),
        fetch(`${API}/rounds/open`, { credentials: 'include' }),
      ]);
      const evtList = evtRes.ok ? await evtRes.json() : [];
      const teeRaw = trRes.ok ? await trRes.json() : [];
      // Transform tee times → event-shaped objects
      const teeShaped = (Array.isArray(teeRaw) ? teeRaw : [])
        .filter(t => {
          if (t.status === 'cancelled') return false;
          if (view === 'past') return t.date < todayStr;
          return t.date >= todayStr;
        })
        .map(t => ({
          event_id: t.round_id,
          source: 'tee_time',
          title: 'Tee Time',
          description: t.description || '',
          event_type: 'tee_time',
          date: t.date,
          time: t.time,
          location: t.course_name,
          club_name: t.course_name,
          max_participants: t.max_players,
          entry_fee: null,
          flyer_image: null,
          external_signup_url: null,
          participants: t.current_players || [],
          participant_details: t.players || [],
          created_by: t.organizer_id,
          created_by_name: t.organizer_name,
          status: 'approved',
          results: null,
        }));
      const merged = [...evtList, ...teeShaped].sort((a, b) => {
        if (view === 'past') return (b.date || '').localeCompare(a.date || '');
        return (a.date || '').localeCompare(b.date || '');
      });
      setEvents(merged);
    } catch (e) {
      console.error('Error fetching events:', e);
    } finally {
      setLoadingEvents(false);
    }
  };

  const selectEvent = async (event) => {
    setSelectedEvent(event);
    // Update URL so users can share/bookmark
    try {
      const next = new URLSearchParams(window.location.search);
      next.set('event', event.event_id);
      setSearchParams(next, { replace: true });
    } catch {}
    if (event.participants?.length > 0) {
      try {
        const res = await fetch(`${API}/events/${event.event_id}/participants`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSelectedEvent(prev => prev ? { ...prev, participant_details: data.participants } : prev);
        }
      } catch {}
    }
  };

  const closeEvent = () => {
    setSelectedEvent(null);
    try {
      const next = new URLSearchParams(window.location.search);
      next.delete('event');
      setSearchParams(next, { replace: true });
    } catch {}
  };

  const handleSignup = async (eventOrId) => {
    const ev = typeof eventOrId === 'string' ? events.find(e => e.event_id === eventOrId) : eventOrId;
    const id = typeof eventOrId === 'string' ? eventOrId : ev?.event_id;
    if (!id) return;
    const isTeeTime = ev?.source === 'tee_time';
    const url = isTeeTime ? `${API}/rounds/open/${id}/join` : `${API}/events/${id}/signup`;
    try {
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      if (response.ok) {
        toast.success('You\'re in! See you on the course.');
        await fetchEvents();
        // Try to refresh selected event
        if (isTeeTime) {
          // re-find merged tee time
          setSelectedEvent(prev => prev ? { ...prev } : prev);
        } else {
          try {
            const refreshed = await (await fetch(`${API}/events/${id}`, { credentials: 'include' })).json();
            setSelectedEvent(refreshed);
          } catch {}
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sign up');
      }
    } catch {
      toast.error('An error occurred');
    }
  };

  const handleCancelSignup = async (eventOrId) => {
    const ev = typeof eventOrId === 'string' ? events.find(e => e.event_id === eventOrId) : eventOrId;
    const id = typeof eventOrId === 'string' ? eventOrId : ev?.event_id;
    if (!id) return;
    const isTeeTime = ev?.source === 'tee_time';
    const url = isTeeTime ? `${API}/rounds/open/${id}/leave` : `${API}/events/${id}/cancel`;
    try {
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      if (response.ok) {
        toast.success(isTeeTime ? 'Left tee time' : 'Signup cancelled');
        fetchEvents();
        setSelectedEvent(null);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch {
      toast.error('An error occurred');
    }
  };

  const handleRemoveParticipant = async (eventOrId, userId, userName) => {
    const ev = typeof eventOrId === 'string' ? events.find(e => e.event_id === eventOrId) : eventOrId;
    const id = typeof eventOrId === 'string' ? eventOrId : ev?.event_id;
    if (!id) return;
    if (!window.confirm(`Remove ${userName} from this ${ev?.source === 'tee_time' ? 'tee time' : 'event'}?`)) return;
    const isTeeTime = ev?.source === 'tee_time';
    const url = isTeeTime
      ? `${API}/rounds/open/${id}/remove-player/${userId}`
      : `${API}/events/${id}/remove-participant/${userId}`;
    try {
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      if (response.ok) {
        toast.success(`${userName} removed`);
        await fetchEvents();
        if (isTeeTime) {
          // Re-grab from refreshed events list
          const refreshed = (await (await fetch(`${API}/rounds/open`, { credentials: 'include' })).json()).find(r => r.round_id === id);
          if (refreshed) {
            setSelectedEvent({
              ...selectedEvent,
              participants: refreshed.current_players || [],
              participant_details: refreshed.players || [],
            });
          }
        } else {
          try {
            const refreshed = await (await fetch(`${API}/events/${id}`, { credentials: 'include' })).json();
            setSelectedEvent(refreshed);
          } catch {}
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to remove');
      }
    } catch {
      toast.error('An error occurred');
    }
  };

  // Render title as "Title / Club Name" (or just title if no club_name)
  const displayTitle = (event) => {
    if (!event) return '';
    if (event.club_name && event.club_name.trim()) {
      return `${event.title} / ${event.club_name}`;
    }
    return event.title;
  };

  const handleSaveResults = async (eventId, resultsPayload) => {
    try {
      const res = await fetch(`${API}/events/${eventId}/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(resultsPayload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save results');
        return;
      }
      const data = await res.json();
      toast.success('Final results posted');
      setPostingResults(false);
      // Refresh
      await fetchEvents();
      const refreshed = await (await fetch(`${API}/events/${eventId}`, { credentials: 'include' })).json();
      setSelectedEvent(refreshed);
    } catch {
      toast.error('Network error');
    }
  };

  const handleClearResults = async (eventId) => {
    if (!window.confirm('Clear posted results?')) return;
    try {
      const res = await fetch(`${API}/events/${eventId}/results`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Results cleared');
        await fetchEvents();
        const refreshed = await (await fetch(`${API}/events/${eventId}`, { credentials: 'include' })).json();
        setSelectedEvent(refreshed);
      }
    } catch {}
  };

  const handleShareEvent = async (event) => {
    const url = `${window.location.origin}/events?event=${event.event_id}`;
    const shareData = {
      title: displayTitle(event),
      text: `Check out this event: ${displayTitle(event)} on ${new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })} at ${event.location}`,
      url,
    };
    try {
      if (navigator.share && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      // user cancelled native share — fall through to clipboard copy
      if (err?.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Event link copied to clipboard');
    } catch {
      toast.error('Could not copy link — please copy manually');
    }
  };

  // URL sync handled imperatively in selectEvent / closeEvent

  const eventTypeMeta = (type) => {
    switch (type) {
      case 'tee_time': return { color: 'bg-emerald-500 text-white', dot: 'bg-emerald-500', label: 'Tee Time' };
      case 'tournament': return { color: 'bg-bundle-gold text-bundle-green-dark', dot: 'bg-bundle-gold', label: 'Tournament' };
      case 'weekly_game': return { color: 'bg-bundle-green text-white', dot: 'bg-bundle-green', label: 'Weekly Game' };
      case 'social': return { color: 'bg-blue-500 text-white', dot: 'bg-blue-500', label: 'Social' };
      case 'casual_round': return { color: 'bg-purple-500 text-white', dot: 'bg-purple-500', label: 'Casual Round' };
      default: return { color: 'bg-gray-500 text-white', dot: 'bg-gray-500', label: type };
    }
  };

  // Filter events
  const filteredEvents = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(e => e.event_type === filter);
  }, [events, filter]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const thisMonth = events.filter(e => {
      const d = new Date(e.date + 'T00:00:00');
      return d >= monthStart && d <= monthEnd;
    });
    const tournaments = events.filter(e => e.event_type === 'tournament');
    const myEvents = events.filter(e => e.participants?.includes(user?.user_id));
    return {
      total: events.length,
      thisMonth: thisMonth.length,
      tournaments: tournaments.length,
      mine: myEvents.length,
    };
  }, [events, user]);

  // Featured (next upcoming) event - prefer tournament
  const featuredEvent = useMemo(() => {
    const sorted = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
    return sorted.find(e => e.event_type === 'tournament') || sorted[0] || null;
  }, [events]);

  // Calendar generation
  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const days = [];
    const prevLast = new Date(year, month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      days.push({ day: prevLast - i, isCurrentMonth: false, date: new Date(year, month - 1, prevLast - i) });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    return days;
  }, [calendarMonth]);

  const eventsOnDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return events.filter(e => e.date === dateStr);
  };

  const isToday = (date) => {
    const t = new Date();
    return date.toDateString() === t.toDateString();
  };

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-12" data-testid="events-page">
      <Navigation user={user} onLogout={logout} />

      {/* HERO HEADER */}
      <div className="relative overflow-hidden bg-gradient-to-br from-bundle-green-dark via-bundle-green to-bundle-green-dark text-white pt-20 md:pt-24 pb-10 md:pb-14">
        {/* Decorative bg */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-bundle-gold blur-3xl"></div>
          <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full bg-bundle-gold blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-bundle-gold/20 border border-bundle-gold/40 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-bundle-gold" />
                <span className="font-body text-xs uppercase tracking-widest text-bundle-gold">The Event Calendar</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl mb-3 leading-tight">
                Tournaments<br />& Member Events
              </h1>
              <p className="font-body text-base md:text-lg text-bundle-cream/90 max-w-xl">
                Every tee time, every shootout, every social — all in one place. Don't miss a single round.
              </p>
            </div>

            {user.is_member && (
              <Button
                data-testid="create-event-btn"
                onClick={() => navigate('/create-event')}
                className="bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-semibold rounded-full px-6 py-6 text-base shadow-lg"
              >
                <PlusCircle className="w-5 h-5 mr-2" />
                Create Event
              </Button>
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-8 md:mt-10">
            <StatCard label="Upcoming" value={stats.total} icon={CalendarIcon} testid="stat-upcoming" />
            <StatCard label="This Month" value={stats.thisMonth} icon={Flame} testid="stat-month" highlight />
            <StatCard label="Tournaments" value={stats.tournaments} icon={Trophy} testid="stat-tournaments" />
            <StatCard label="You're In" value={stats.mine} icon={Check} testid="stat-mine" />
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 -mt-6 md:-mt-8 relative">

        {/* Upcoming / Past toggle */}
        <div className="mb-5 flex justify-center">
          <div className="inline-flex bg-white border border-bundle-cream-dark rounded-full p-1 shadow-sm" data-testid="event-view-toggle">
            <button
              onClick={() => setView('upcoming')}
              data-testid="view-upcoming"
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                view === 'upcoming' ? 'bg-bundle-green text-white shadow-md' : 'text-bundle-green hover:bg-bundle-cream'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setView('past')}
              data-testid="view-past"
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                view === 'past' ? 'bg-bundle-green text-white shadow-md' : 'text-bundle-green hover:bg-bundle-cream'
              }`}
            >
              Past Events
            </button>
          </div>
        </div>

        {/* FEATURED EVENT SPOTLIGHT (upcoming only) */}
        {view === 'upcoming' && featuredEvent && (
          <FeaturedEventCard
            event={featuredEvent}
            user={user}
            onView={() => selectEvent(featuredEvent)}
            onSignup={() => handleSignup(featuredEvent)}
            meta={eventTypeMeta(featuredEvent.event_type)}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
          {/* CALENDAR (Sticky on desktop) */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <Card className="bg-white border-bundle-cream-dark shadow-md overflow-hidden" data-testid="events-calendar">
                <div className="bg-bundle-green text-white px-4 py-3 flex items-center justify-between">
                  <button
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="cal-prev-btn"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h3 className="font-heading text-base font-bold">
                    {MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                  </h3>
                  <button
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="cal-next-btn"
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-3">
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS.map((d, i) => (
                      <div key={i} className="text-center text-[10px] font-bold uppercase text-gray-400 tracking-wider py-1">
                        {d}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((d, i) => {
                      const dayEvents = eventsOnDate(d.date);
                      const isSelected = isSameDay(d.date, selectedDate);
                      const today = isToday(d.date);
                      return (
                        <button
                          key={i}
                          onClick={() => setSelectedDate(isSelected ? null : d.date)}
                          className={`
                            relative aspect-square rounded-md text-xs font-medium transition-all
                            flex flex-col items-center justify-center
                            ${!d.isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                            ${today && !isSelected ? 'ring-2 ring-bundle-gold ring-inset' : ''}
                            ${isSelected ? 'bg-bundle-green text-white shadow-md scale-105' : 'hover:bg-bundle-cream'}
                            ${dayEvents.length > 0 && !isSelected ? 'font-bold text-bundle-green' : ''}
                          `}
                          data-testid={`cal-day-${d.day}`}
                        >
                          <span>{d.day}</span>
                          {dayEvents.length > 0 && (
                            <div className="flex gap-0.5 mt-0.5">
                              {dayEvents.slice(0, 3).map((e, idx) => (
                                <span key={idx} className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : eventTypeMeta(e.event_type).dot}`}></span>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected date events */}
                {selectedDate && (
                  <div className="border-t border-bundle-cream-dark p-4 bg-bundle-cream/30">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-heading text-sm font-bold text-bundle-green">
                        {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      </p>
                      <button
                        onClick={() => setSelectedDate(null)}
                        className="text-xs text-gray-500 hover:text-bundle-green"
                      >
                        Clear
                      </button>
                    </div>
                    {eventsOnDate(selectedDate).length === 0 ? (
                      <p className="font-body text-xs text-gray-500 text-center py-4">No events on this date</p>
                    ) : (
                      <div className="space-y-2">
                        {eventsOnDate(selectedDate).map(e => {
                          const m = eventTypeMeta(e.event_type);
                          return (
                            <button
                              key={e.event_id}
                              onClick={() => selectEvent(e)}
                              className="w-full text-left p-2.5 bg-white rounded-lg hover:shadow-md transition-shadow border border-bundle-cream-dark"
                              data-testid={`cal-event-${e.event_id}`}
                            >
                              <div className="flex items-start gap-2">
                                <span className={`w-1.5 h-10 rounded-full ${m.dot} flex-shrink-0`}></span>
                                <div className="min-w-0 flex-1">
                                  <p className="font-body text-xs text-gray-500 mb-0.5">{e.time}</p>
                                  <p className="font-body font-semibold text-sm text-bundle-green truncate">{e.title}</p>
                                  <p className="font-body text-xs text-gray-500 truncate">{e.location}</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Legend */}
                <div className="border-t border-bundle-cream-dark p-3 bg-white">
                  <p className="font-body text-[10px] uppercase tracking-wider text-gray-400 mb-2">Legend</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {EVENT_FILTERS.slice(1).map(f => {
                      const m = eventTypeMeta(f.id);
                      return (
                        <div key={f.id} className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${m.dot}`}></span>
                          <span className="font-body text-[11px] text-gray-600">{f.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* EVENTS LIST */}
          <div className="lg:col-span-3">
            {/* Filter chips */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-testid="event-filters">
              {EVENT_FILTERS.map(f => {
                const Icon = f.icon;
                const isActive = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`
                      flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm font-medium whitespace-nowrap
                      transition-all flex-shrink-0
                      ${isActive
                        ? 'bg-bundle-green text-white shadow-md'
                        : 'bg-white text-bundle-green border border-bundle-cream-dark hover:border-bundle-green'}
                    `}
                    data-testid={`filter-${f.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {f.label}
                  </button>
                );
              })}
            </div>

            <h2 className="font-heading font-bold text-2xl text-bundle-green mb-4 flex items-center gap-2">
              {view === 'past'
                ? (filter === 'all' ? 'Past Events' : `Past ${EVENT_FILTERS.find(f => f.id === filter)?.label}`)
                : (filter === 'all' ? 'All Upcoming Events' : EVENT_FILTERS.find(f => f.id === filter)?.label)}
              <span className="text-sm font-body font-normal text-gray-500">({filteredEvents.length})</span>
            </h2>

            {loadingEvents ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
              </div>
            ) : filteredEvents.length === 0 ? (
              <Card className="bg-white border-bundle-cream-dark border-dashed">
                <CardContent className="p-12 text-center">
                  <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-bundle-green mb-2">
                    {view === 'past'
                      ? (filter === 'all' ? 'No Past Events Yet' : `No Past ${EVENT_FILTERS.find(f => f.id === filter)?.label} Yet`)
                      : (filter === 'all' ? 'No Upcoming Events Yet' : `No ${EVENT_FILTERS.find(f => f.id === filter)?.label} Yet`)}
                  </h3>
                  <p className="font-body text-gray-500 mb-4">
                    {view === 'past' ? 'Past events will show here once tournaments wrap up.' : 'Be the first to put one on the calendar.'}
                  </p>
                  {view === 'upcoming' && user.is_member && (
                    <Button onClick={() => navigate('/create-event')} className="btn-bundle">
                      <PlusCircle className="w-4 h-4 mr-2" />Create the first event
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="events-list">
                {filteredEvents.map(event => (
                  <PremiumEventCard
                    key={event.event_id}
                    event={event}
                    user={user}
                    meta={eventTypeMeta(event.event_type)}
                    onClick={() => selectEvent(event)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Post Results Dialog */}
        <PostResultsDialog
          event={postingResults}
          onClose={() => setPostingResults(null)}
          onSave={(payload) => handleSaveResults(postingResults.event_id, payload)}
        />

        {/* Event Detail Modal */}
        <Dialog open={!!selectedEvent} onOpenChange={(open) => { if (!open) closeEvent(); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
            {selectedEvent && (
              <>
                {selectedEvent.flyer_image && (
                  <div className="relative">
                    <img
                      src={`${window.location.origin}${selectedEvent.flyer_image}`}
                      alt={`${displayTitle(selectedEvent)} flyer`}
                      data-testid="event-modal-flyer"
                      className="w-full max-h-72 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <span className={`absolute top-3 left-3 inline-block px-3 py-1 text-xs rounded-full font-body font-semibold ${eventTypeMeta(selectedEvent.event_type).color}`}>
                      {eventTypeMeta(selectedEvent.event_type).label}
                    </span>
                  </div>
                )}
                <div className="p-6">
                  <DialogHeader>
                    {!selectedEvent.flyer_image && (
                      <span className={`inline-block px-3 py-1 text-xs rounded-full font-body font-semibold w-fit mb-3 ${eventTypeMeta(selectedEvent.event_type).color}`}>
                        {eventTypeMeta(selectedEvent.event_type).label}
                      </span>
                    )}
                    <DialogTitle className="font-heading text-2xl text-bundle-green text-left">
                      {selectedEvent.title}
                      {selectedEvent.club_name && (
                        <span className="block text-base font-body font-normal text-bundle-gold-dark mt-0.5">
                          / {selectedEvent.club_name}
                        </span>
                      )}
                    </DialogTitle>
                    {selectedEvent.description && (
                      <DialogDescription className="font-body text-gray-600 text-left whitespace-pre-wrap">
                        {selectedEvent.description}
                      </DialogDescription>
                    )}
                    {selectedEvent.is_fundraiser && (selectedEvent.charity_info || selectedEvent.sponsor_info) && (
                      <div className="mt-3 bg-bundle-gold/10 border border-bundle-gold/40 rounded-xl p-3 space-y-2">
                        {selectedEvent.charity_info && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-bundle-green/70 font-bold mb-0.5">Benefiting</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedEvent.charity_info}</p>
                          </div>
                        )}
                        {selectedEvent.sponsor_info && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-bundle-green/70 font-bold mb-0.5">Sponsors</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedEvent.sponsor_info}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => handleShareEvent(selectedEvent)}
                      className="inline-flex items-center gap-1.5 self-start mt-2 px-3 py-1.5 rounded-full bg-bundle-cream hover:bg-bundle-cream-dark transition-colors font-body text-xs font-semibold text-bundle-green"
                      data-testid="event-share-btn"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Share Event
                    </button>
                  </DialogHeader>

                  <div className="grid grid-cols-2 gap-3 my-5">
                    <DetailTile icon={CalendarIcon} label="Date" value={new Date(selectedEvent.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} />
                    <DetailTile icon={Clock} label="Time" value={selectedEvent.time} />
                    <DetailTile icon={MapPin} label="Location" value={selectedEvent.location} />
                    {selectedEvent.entry_fee ? (
                      <DetailTile icon={DollarSign} label="Entry" value={`$${selectedEvent.entry_fee}`} accent />
                    ) : (
                      <DetailTile icon={DollarSign} label="Entry" value="Free" />
                    )}
                  </div>

                  {selectedEvent.max_participants && (
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-body text-xs text-gray-500 uppercase tracking-wider">Spots Filled</span>
                        <span className="font-heading font-bold text-bundle-green">
                          {selectedEvent.participants?.length || 0} / {selectedEvent.max_participants}
                        </span>
                      </div>
                      <div className="h-2 bg-bundle-cream rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-bundle-green to-bundle-gold transition-all"
                          style={{ width: `${Math.min(100, ((selectedEvent.participants?.length || 0) / selectedEvent.max_participants) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {selectedEvent.created_by_name && (
                    <div className="flex items-center gap-2 mb-4 pb-4 border-b border-bundle-cream-dark">
                      <div className="w-8 h-8 rounded-full bg-bundle-gold/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-bundle-gold-dark" />
                      </div>
                      <div>
                        <p className="font-body text-xs text-gray-500">Hosted by</p>
                        <p className="font-body font-semibold text-bundle-green text-sm">{selectedEvent.created_by_name}</p>
                      </div>
                    </div>
                  )}

                  {selectedEvent.results && (
                    <FinalResultsDisplay
                      results={selectedEvent.results}
                      canEdit={selectedEvent.created_by === user.user_id || user.is_admin}
                      onEdit={() => setPostingResults(selectedEvent)}
                      onClear={() => handleClearResults(selectedEvent.event_id)}
                    />
                  )}

                  {selectedEvent.participant_details && selectedEvent.participant_details.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-body text-xs uppercase tracking-wider text-gray-500">
                          Players Confirmed ({selectedEvent.participant_details.length})
                        </p>
                        {(selectedEvent.created_by === user.user_id || user.is_admin) && (
                          <span className="text-[10px] text-bundle-gold-dark font-bold uppercase tracking-wider">
                            Manage
                          </span>
                        )}
                      </div>
                      {(selectedEvent.created_by === user.user_id || user.is_admin) ? (
                        <div className="space-y-1.5" data-testid="manage-participants">
                          {selectedEvent.participant_details.map(p => (
                            <div key={p.user_id} className="flex items-center gap-2 bg-bundle-cream/40 px-3 py-2 rounded-lg">
                              {p.picture ? (
                                <img src={p.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 bg-bundle-green rounded-full flex items-center justify-center text-white text-xs font-bold">
                                  {p.name?.charAt(0)}
                                </div>
                              )}
                              <span className="font-body text-sm text-bundle-green flex-1 truncate">{p.name}</span>
                              {p.user_id !== user.user_id && (
                                <button
                                  onClick={() => handleRemoveParticipant(selectedEvent, p.user_id, p.name)}
                                  data-testid={`remove-participant-${p.user_id}`}
                                  className="p-1.5 rounded-full text-red-500 hover:bg-red-50 transition-colors"
                                  title="Remove from event"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {selectedEvent.participant_details.map(p => (
                            <div key={p.user_id} className="flex items-center gap-2 bg-bundle-cream/60 px-3 py-1.5 rounded-full">
                              {p.picture ? (
                                <img src={p.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 bg-bundle-green rounded-full flex items-center justify-center text-white text-xs font-bold">
                                  {p.name?.charAt(0)}
                                </div>
                              )}
                              <span className="font-body text-sm text-bundle-green">{p.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <DialogFooter className="flex flex-col gap-2 mt-2">
                    {/* Post Final Results — past events, creator/admin only, when not yet posted */}
                    {(() => {
                      const isPast = new Date(selectedEvent.date + 'T00:00:00') < new Date(new Date().toDateString());
                      const canManage = selectedEvent.created_by === user.user_id || user.is_admin;
                      if (isPast && canManage && !selectedEvent.results) {
                        return (
                          <Button
                            data-testid="post-results-btn"
                            onClick={() => setPostingResults(selectedEvent)}
                            className="w-full bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold"
                          >
                            <Trophy className="w-4 h-4 mr-2" /> Post Final Results
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    {(selectedEvent.created_by === user.user_id || user.is_admin) && selectedEvent.source !== 'tee_time' && (
                      <Button
                        data-testid="event-edit-button"
                        onClick={() => { setSelectedEvent(null); navigate(`/events/edit/${selectedEvent.event_id}`); }}
                        variant="outline"
                        className="w-full border-bundle-green text-bundle-green hover:bg-bundle-green/5"
                      >
                        <Pencil className="w-4 h-4 mr-2" />Edit Event
                      </Button>
                    )}
                    {(() => {
                      const isPast = new Date(selectedEvent.date + 'T00:00:00') < new Date(new Date().toDateString());
                      if (isPast) return null;
                      return selectedEvent.participants?.includes(user.user_id) ? (
                        <Button
                          data-testid="event-cancel-btn"
                          onClick={() => handleCancelSignup(selectedEvent)}
                          variant="outline"
                          className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        >
                          {selectedEvent.source === 'tee_time' && selectedEvent.created_by === user.user_id
                            ? 'Cancel Tee Time'
                            : selectedEvent.source === 'tee_time' ? 'Leave Tee Time' : 'Cancel My Signup'}
                        </Button>
                      ) : (
                        <Button
                          data-testid="event-signup-btn"
                          onClick={() => handleSignup(selectedEvent)}
                          disabled={selectedEvent.max_participants && selectedEvent.participants?.length >= selectedEvent.max_participants}
                          className="w-full btn-bundle text-base py-6"
                        >
                          {selectedEvent.max_participants && selectedEvent.participants?.length >= selectedEvent.max_participants
                            ? 'Event Full'
                            : selectedEvent.source === 'tee_time' ? 'Join Tee Time' : 'Count Me In'}
                        </Button>
                      );
                    })()}
                    {selectedEvent.external_signup_url && (
                      <Button
                        asChild
                        variant="outline"
                        className="w-full border-bundle-gold text-bundle-gold-dark hover:bg-bundle-gold/10 text-base py-6"
                        data-testid="event-external-signup-btn"
                      >
                        <a
                          href={selectedEvent.external_signup_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Sign Up at Official Site
                        </a>
                      </Button>
                    )}
                  </DialogFooter>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ─────────── Sub-components ───────────

function StatCard({ label, value, icon: Icon, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`
        rounded-xl px-4 py-3 backdrop-blur-sm
        ${highlight
          ? 'bg-bundle-gold/15 border border-bundle-gold/40'
          : 'bg-white/10 border border-white/20'}
      `}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${highlight ? 'text-bundle-gold' : 'text-bundle-cream/70'}`} />
        <span className="font-body text-[11px] uppercase tracking-wider text-bundle-cream/80">{label}</span>
      </div>
      <p className="font-heading text-2xl md:text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function FeaturedEventCard({ event, user, meta, onView, onSignup }) {
  const isSignedUp = event.participants?.includes(user?.user_id);
  const isFull = event.max_participants && event.participants?.length >= event.max_participants;
  const dateObj = new Date(event.date + 'T00:00:00');
  const daysAway = Math.ceil((dateObj - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <Card
      onClick={onView}
      className="bg-white border-2 border-bundle-gold/40 shadow-2xl overflow-hidden cursor-pointer hover:shadow-bundle-gold/20 transition-shadow"
      data-testid="featured-event-card"
    >
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Flyer / Image side */}
          <div className="md:col-span-2 relative bg-gradient-to-br from-bundle-green-dark to-bundle-green min-h-[220px] md:min-h-[280px]">
            {event.flyer_image ? (
              <img
                src={`${window.location.origin}${event.flyer_image}`}
                alt={event.title}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="featured-flyer"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Trophy className="w-24 h-24 text-bundle-gold/40" />
              </div>
            )}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 bg-bundle-gold rounded-full">
              <Sparkles className="w-3 h-3 text-bundle-green-dark" />
              <span className="font-body text-[11px] font-bold text-bundle-green-dark uppercase tracking-wider">Up Next</span>
            </div>
          </div>

          {/* Detail side */}
          <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`inline-block px-2.5 py-1 text-[11px] rounded-full font-body font-semibold ${meta.color}`}>
                  {meta.label}
                </span>
                {event.is_fundraiser && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full font-body font-bold bg-bundle-gold/20 text-bundle-green border border-bundle-gold/40 uppercase tracking-wider">
                    Fundraiser
                  </span>
                )}
                {daysAway >= 0 && daysAway <= 7 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full font-body font-semibold bg-red-100 text-red-700">
                    <Flame className="w-3 h-3" />
                    {daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `${daysAway} days away`}
                  </span>
                )}
                {isSignedUp && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full font-body font-semibold bg-bundle-green/10 text-bundle-green">
                    <Check className="w-3 h-3" />You're In
                  </span>
                )}
              </div>

              <h2 className="font-heading text-2xl md:text-3xl font-bold text-bundle-green mb-3 leading-tight">
                {event.title}
                {event.club_name && (
                  <span className="block text-base md:text-lg font-body font-normal text-bundle-gold-dark mt-0.5">
                    / {event.club_name}
                  </span>
                )}
              </h2>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-bundle-gold flex-shrink-0" />
                  <span className="font-body text-sm text-gray-700">
                    {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-bundle-gold flex-shrink-0" />
                  <span className="font-body text-sm text-gray-700">{event.time}</span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <MapPin className="w-4 h-4 text-bundle-gold flex-shrink-0" />
                  <span className="font-body text-sm text-gray-700 truncate">{event.location}</span>
                </div>
                {event.entry_fee ? (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-bundle-gold flex-shrink-0" />
                    <span className="font-body text-sm text-gray-700">${event.entry_fee} entry</span>
                  </div>
                ) : null}
                {event.max_participants && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-bundle-gold flex-shrink-0" />
                    <span className="font-body text-sm text-gray-700">
                      {event.participants?.length || 0}/{event.max_participants} spots
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={(e) => { e.stopPropagation(); onView(); }}
                variant="outline"
                className="flex-1 min-w-[140px] border-bundle-green text-bundle-green hover:bg-bundle-green hover:text-white"
                data-testid="featured-view-btn"
              >
                See Details<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {!isSignedUp && !isFull && (
                <Button
                  onClick={(e) => { e.stopPropagation(); onSignup(); }}
                  className="flex-1 min-w-[140px] bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-semibold"
                  data-testid="featured-signup-btn"
                >
                  Count Me In
                </Button>
              )}
              {event.external_signup_url && (
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 min-w-[140px] border-bundle-gold text-bundle-gold-dark hover:bg-bundle-gold/10"
                  data-testid="featured-external-btn"
                >
                  <a
                    href={event.external_signup_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Official Sign-up<ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PremiumEventCard({ event, user, meta, onClick }) {
  const isSignedUp = event.participants?.includes(user?.user_id);
  const isFull = event.max_participants && event.participants?.length >= event.max_participants;
  const dateObj = new Date(event.date + 'T00:00:00');
  const fillPct = event.max_participants
    ? Math.min(100, ((event.participants?.length || 0) / event.max_participants) * 100)
    : 0;

  return (
    <Card
      onClick={onClick}
      className="bg-white border border-bundle-cream-dark hover:border-bundle-gold hover:shadow-xl transition-all cursor-pointer overflow-hidden group"
      data-testid={`event-card-${event.event_id}`}
    >
      <CardContent className="p-0">
        <div className="flex">
          {/* Date strip */}
          <div className={`flex-shrink-0 w-20 md:w-24 ${meta.color} flex flex-col items-center justify-center py-4`}>
            <span className="font-body text-[10px] font-bold uppercase tracking-widest opacity-80">
              {dateObj.toLocaleDateString('en-US', { month: 'short' })}
            </span>
            <span className="font-heading text-3xl md:text-4xl font-bold leading-none my-1">
              {dateObj.getDate()}
            </span>
            <span className="font-body text-[10px] font-medium opacity-80">
              {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
            </span>
          </div>

          {/* Body */}
          <div className="flex-1 p-4 md:p-5 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full font-body font-semibold ${meta.color}`}>
                    {meta.label}
                  </span>
                  {event.is_fundraiser && (
                    <span className="inline-block px-2 py-0.5 text-[10px] rounded-full font-body font-bold bg-bundle-gold/20 text-bundle-green border border-bundle-gold/40 uppercase tracking-wider">
                      Fundraiser
                    </span>
                  )}
                  {isSignedUp && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-body font-semibold bg-bundle-green/10 text-bundle-green">
                      <Check className="w-2.5 h-2.5" />Joined
                    </span>
                  )}
                  {isFull && !isSignedUp && (
                    <span className="inline-block px-2 py-0.5 text-[10px] rounded-full font-body font-semibold bg-red-100 text-red-700">
                      Full
                    </span>
                  )}
                  {event.external_signup_url && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full font-body font-semibold bg-bundle-gold/15 text-bundle-gold-dark border border-bundle-gold/30">
                      <ExternalLink className="w-2.5 h-2.5" />Official Link
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-bundle-green leading-tight truncate group-hover:text-bundle-gold-dark transition-colors">
                  {event.title}
                  {event.club_name && (
                    <span className="block text-xs md:text-sm font-body font-normal text-bundle-gold-dark mt-0.5 truncate">
                      / {event.club_name}
                    </span>
                  )}
                </h3>
              </div>
              <ArrowRight className="w-5 h-5 text-bundle-green/30 group-hover:text-bundle-gold group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600 font-body mb-3">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-bundle-gold" />{event.time}
              </span>
              <span className="flex items-center gap-1 truncate max-w-[180px]">
                <MapPin className="w-3.5 h-3.5 text-bundle-gold" />{event.location}
              </span>
              {event.entry_fee ? (
                <span className="flex items-center gap-1 text-bundle-gold-dark font-semibold">
                  <DollarSign className="w-3.5 h-3.5" />${event.entry_fee}
                </span>
              ) : (
                <span className="text-green-700 font-semibold">Free</span>
              )}
            </div>

            {event.results?.winner && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-bundle-gold/15 to-bundle-gold/5 border border-bundle-gold/30">
                <Trophy className="w-4 h-4 text-bundle-gold-dark flex-shrink-0" />
                <span className="text-xs font-bold text-bundle-gold-dark">Winner:</span>
                <span className="text-xs font-bold text-bundle-green truncate flex-1">{event.results.winner.name}</span>
                {event.results.winner.score && <span className="text-xs font-bold text-bundle-green">{event.results.winner.score}</span>}
                {event.results.winner.payout ? <span className="text-xs font-bold text-emerald-700">${event.results.winner.payout}</span> : null}
              </div>
            )}

            {event.max_participants && !event.results && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-body text-[10px] uppercase tracking-wider text-gray-400">
                    {event.participants?.length || 0}/{event.max_participants} spots
                  </span>
                  {fillPct >= 75 && (
                    <span className="font-body text-[10px] font-bold text-red-600 uppercase">Filling Fast</span>
                  )}
                </div>
                <div className="h-1.5 bg-bundle-cream rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      fillPct >= 100 ? 'bg-red-500' :
                      fillPct >= 75 ? 'bg-bundle-gold' : 'bg-bundle-green'
                    }`}
                    style={{ width: `${fillPct}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent ? 'bg-bundle-gold/10 border border-bundle-gold/30' : 'bg-bundle-cream/50'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent ? 'text-bundle-gold-dark' : 'text-bundle-gold'}`} />
        <span className="font-body text-[10px] uppercase tracking-wider text-gray-500">{label}</span>
      </div>
      <p className={`font-heading font-bold ${accent ? 'text-bundle-gold-dark' : 'text-bundle-green'} text-sm`}>{value}</p>
    </div>
  );
}


function FinalResultsDisplay({ results, canEdit, onEdit, onClear }) {
  if (!results) return null;
  return (
    <div className="mb-5 rounded-2xl bg-gradient-to-br from-bundle-gold/10 via-amber-50 to-bundle-gold/5 border-2 border-bundle-gold/40 overflow-hidden" data-testid="final-results-display">
      <div className="bg-gradient-to-r from-bundle-gold-dark to-bundle-gold px-4 py-2.5 flex items-center gap-2 text-bundle-green-dark">
        <Trophy className="w-5 h-5" />
        <span className="font-heading font-black text-base uppercase tracking-wider">Final Results</span>
        {canEdit && (
          <div className="ml-auto flex gap-1">
            <button onClick={onEdit} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-bundle-green-dark/20 hover:bg-bundle-green-dark/30">
              Edit
            </button>
            <button onClick={onClear} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-900">
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {results.winner && (
          <div className="rounded-xl bg-white border border-bundle-gold/30 p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-bundle-gold flex items-center justify-center flex-shrink-0">
              <Award className="w-5 h-5 text-bundle-green-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-gold-dark">Winner</p>
              <p className="font-heading font-black text-bundle-green text-base truncate">{results.winner.name}</p>
            </div>
            <div className="text-right flex-shrink-0">
              {results.winner.score && <p className="font-heading font-bold text-bundle-green">{results.winner.score}</p>}
              {results.winner.payout ? <p className="text-xs font-bold text-emerald-700">${results.winner.payout}</p> : null}
            </div>
          </div>
        )}

        {results.low_net && (
          <div className="rounded-xl bg-white border border-bundle-cream-dark p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-bundle-green/10 flex items-center justify-center flex-shrink-0">
              <Medal className="w-4 h-4 text-bundle-green" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70">Low Net</p>
              <p className="font-bold text-bundle-green text-sm truncate">{results.low_net.name}</p>
            </div>
            <div className="text-right">
              {results.low_net.score && <p className="text-sm font-bold text-bundle-green">{results.low_net.score}</p>}
              {results.low_net.payout ? <p className="text-xs font-bold text-emerald-700">${results.low_net.payout}</p> : null}
            </div>
          </div>
        )}

        {results.leaderboard && results.leaderboard.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 mb-1.5">Leaderboard</p>
            <div className="rounded-xl bg-white border border-bundle-cream-dark overflow-hidden">
              {results.leaderboard.map((row, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 text-sm ${i !== 0 ? 'border-t border-bundle-cream-dark/60' : ''}`}>
                  <span className={`w-6 text-center font-black ${i === 0 ? 'text-bundle-gold-dark' : i === 1 ? 'text-gray-500' : i === 2 ? 'text-amber-700' : 'text-gray-400'}`}>
                    {row.rank ?? i + 1}
                  </span>
                  <span className="flex-1 text-bundle-green truncate">{row.name}</span>
                  {row.score && <span className="font-bold text-bundle-green text-xs">{row.score}</span>}
                  {row.payout ? <span className="text-xs font-bold text-emerald-700 ml-2">${row.payout}</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {results.skins && results.skins.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 mb-1.5">Skins</p>
            <div className="grid grid-cols-2 gap-1.5">
              {results.skins.map((s, i) => (
                <div key={i} className="bg-white rounded-lg border border-bundle-cream-dark px-2.5 py-1.5 text-xs flex items-center gap-1.5">
                  <span className="font-black text-bundle-gold-dark">#{s.hole}</span>
                  <span className="text-bundle-green truncate flex-1">{s.name}</span>
                  {s.payout ? <span className="text-emerald-700 font-bold">${s.payout}</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {results.notes && (
          <p className="text-xs text-gray-600 italic bg-white rounded-lg border border-bundle-cream-dark p-3">
            {results.notes}
          </p>
        )}

        {results.total_purse ? (
          <div className="text-xs text-center text-gray-500">
            Total purse paid: <span className="font-bold text-emerald-700">${results.total_purse}</span>
          </div>
        ) : null}

        {results.posted_by_name && (
          <p className="text-[10px] text-gray-400 text-center">Posted by {results.posted_by_name}</p>
        )}
      </div>
    </div>
  );
}

function PostResultsDialog({ event, onClose, onSave }) {
  const [winner, setWinner] = React.useState({ name: '', score: '', payout: '' });
  const [lowNet, setLowNet] = React.useState({ name: '', score: '', payout: '' });
  const [leaderboard, setLeaderboard] = React.useState([]);
  const [skins, setSkins] = React.useState([]);
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [showScanner, setShowScanner] = React.useState(false);

  React.useEffect(() => {
    if (event) {
      const ex = event.results || {};
      setWinner(ex.winner || { name: '', score: '', payout: '' });
      setLowNet(ex.low_net || { name: '', score: '', payout: '' });
      setLeaderboard(ex.leaderboard || []);
      setSkins(ex.skins || []);
      setNotes(ex.notes || '');
    }
  }, [event]);

  // Convert scanner output to results payload + auto-fill the form
  const handleScanComplete = (scanResult) => {
    setShowScanner(false);
    if (!scanResult || !scanResult.data) {
      toast.error('Scan returned no data');
      return;
    }
    const players = scanResult.data.players || [];
    if (players.length === 0) {
      toast.error('No players found in scan');
      return;
    }

    // Build leaderboard sorted by total gross (lowest first)
    const playerRows = players
      .map(p => {
        const total = p.scores?.total ?? p.total ?? null;
        const par = p.totalPar ?? p.par ?? null;
        const scoreToPar = (total != null && par != null) ? total - par : null;
        const skinCount = p.skins ?? 0;
        return {
          name: p.name || 'Unknown',
          user_id: p.member_id || null,
          total: total,
          scoreLabel: total != null ? (scoreToPar != null ? `${total} (${scoreToPar > 0 ? '+' : ''}${scoreToPar})` : `${total}`) : null,
          skinCount,
        };
      })
      .filter(p => p.total != null)
      .sort((a, b) => a.total - b.total);

    if (playerRows.length === 0) {
      toast.error('No valid scores in scan');
      return;
    }

    // Winner = lowest gross
    const w = playerRows[0];
    setWinner({ name: w.name, score: w.scoreLabel || '', payout: '' });

    // Leaderboard rows
    setLeaderboard(playerRows.slice(0, 10).map((p, i) => ({
      rank: i + 1,
      name: p.name,
      user_id: p.user_id,
      score: p.scoreLabel || '',
      payout: '',
    })));

    // Skins from scanner output
    if (scanResult.skins?.winners) {
      const skinRows = [];
      Object.entries(scanResult.skins.winners).forEach(([hole, globalId]) => {
        if (!globalId) return;
        const winnerName = scanResult.skins.playerMap?.[globalId]?.name;
        if (winnerName) {
          skinRows.push({ hole: parseInt(hole), name: winnerName, payout: '' });
        }
      });
      skinRows.sort((a, b) => a.hole - b.hole);
      setSkins(skinRows);
    }

    // Notes
    const courseName = scanResult.data.course_name;
    if (courseName) {
      setNotes(prev => prev || `Auto-imported from scanned scorecard: ${courseName}`);
    }

    toast.success(`Auto-filled with ${playerRows.length} players${scanResult.skins?.totalSkins ? ` + ${scanResult.skins.totalSkins} skins` : ''}`);
  };

  if (!event && !showScanner) return null;

  const addLB = () => setLeaderboard([...leaderboard, { rank: leaderboard.length + 1, name: '', score: '', payout: '' }]);
  const updateLB = (i, key, val) => setLeaderboard(leaderboard.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const removeLB = (i) => setLeaderboard(leaderboard.filter((_, idx) => idx !== i));

  const addSkin = () => setSkins([...skins, { hole: 1, name: '', payout: '' }]);
  const updateSkin = (i, key, val) => setSkins(skins.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const removeSkin = (i) => setSkins(skins.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!winner.name?.trim()) {
      toast.error('Winner name is required');
      return;
    }
    setSaving(true);
    const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
    const cleanRow = (r) => ({ rank: r.rank ?? null, name: r.name?.trim() || '', score: r.score?.trim() || null, payout: num(r.payout) });
    const totalPurse =
      (num(winner.payout) || 0) +
      (num(lowNet.payout) || 0) +
      leaderboard.reduce((s, r) => s + (num(r.payout) || 0), 0) +
      skins.reduce((s, r) => s + (num(r.payout) || 0), 0);
    const payload = {
      winner: cleanRow(winner),
      low_net: lowNet.name?.trim() ? cleanRow(lowNet) : null,
      leaderboard: leaderboard.filter(r => r.name?.trim()).map(cleanRow),
      skins: skins.filter(s => s.name?.trim()).map(s => ({ hole: parseInt(s.hole) || 1, name: s.name.trim(), payout: num(s.payout) })),
      notes: notes.trim() || null,
      total_purse: totalPurse > 0 ? totalPurse : null,
    };
    await onSave(payload);
    setSaving(false);
  };

  // Render scorecard scanner overlay
  if (showScanner) {
    return (
      <div className="fixed inset-0 z-[60] bg-black">
        <ScorecardScanner
          onScanComplete={handleScanComplete}
          onClose={() => setShowScanner(false)}
        />
      </div>
    );
  }

  return (
    <Dialog open={!!event} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="post-results-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-bundle-green flex items-center gap-2">
            <Trophy className="w-5 h-5 text-bundle-gold" /> Post Final Results
          </DialogTitle>
          <DialogDescription>{event?.title}{event?.club_name ? ` / ${event.club_name}` : ''}</DialogDescription>
        </DialogHeader>

        {/* AI Auto-fill CTA */}
        <div className="rounded-xl bg-gradient-to-br from-bundle-green/5 to-bundle-gold/5 border border-bundle-green/20 p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-bundle-gold/20 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-5 h-5 text-bundle-gold-dark" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-bundle-green text-sm">Auto-fill from scorecard</p>
            <p className="text-[11px] text-gray-600">Snap or upload the scorecard — winner, leaderboard, and skins fill automatically.</p>
          </div>
          <Button
            onClick={() => setShowScanner(true)}
            data-testid="auto-fill-scan-btn"
            size="sm"
            className="bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold flex-shrink-0"
          >
            <Camera className="w-4 h-4 mr-1" /> Scan
          </Button>
        </div>

        <div className="space-y-4 py-2">
          <div className="rounded-xl border-2 border-bundle-gold/40 bg-bundle-gold/5 p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-gold-dark flex items-center gap-1">
              <Award className="w-3.5 h-3.5" /> Winner *
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="winner-name-input" placeholder="Name" value={winner.name} onChange={e => setWinner({...winner, name: e.target.value})} />
              <Input placeholder="Score (-7)" value={winner.score} onChange={e => setWinner({...winner, score: e.target.value})} />
              <Input placeholder="Payout $" type="number" value={winner.payout} onChange={e => setWinner({...winner, payout: e.target.value})} />
            </div>
          </div>

          <div className="rounded-xl border border-bundle-cream-dark bg-white p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 flex items-center gap-1">
              <Medal className="w-3.5 h-3.5" /> Low Net (optional)
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Name" value={lowNet.name} onChange={e => setLowNet({...lowNet, name: e.target.value})} />
              <Input placeholder="Net 68" value={lowNet.score} onChange={e => setLowNet({...lowNet, score: e.target.value})} />
              <Input placeholder="Payout $" type="number" value={lowNet.payout} onChange={e => setLowNet({...lowNet, payout: e.target.value})} />
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 mb-1.5">Top Finishers (optional)</p>
            <div className="space-y-1.5">
              {leaderboard.map((row, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <Input className="w-12 text-center" type="number" placeholder="#" value={row.rank ?? ''} onChange={e => updateLB(i, 'rank', parseInt(e.target.value) || null)} />
                  <Input className="flex-1" placeholder="Name" value={row.name} onChange={e => updateLB(i, 'name', e.target.value)} />
                  <Input className="w-20" placeholder="Score" value={row.score || ''} onChange={e => updateLB(i, 'score', e.target.value)} />
                  <Input className="w-20" type="number" placeholder="$" value={row.payout || ''} onChange={e => updateLB(i, 'payout', e.target.value)} />
                  <button onClick={() => removeLB(i)} className="text-red-500 hover:bg-red-50 p-1 rounded"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <Button onClick={addLB} variant="outline" size="sm" className="w-full mt-2 text-xs" data-testid="add-leaderboard-row">
              <Plus className="w-3 h-3 mr-1" /> Add finisher
            </Button>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 mb-1.5">Skins (optional)</p>
            <div className="space-y-1.5">
              {skins.map((s, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <Input className="w-14 text-center" type="number" min="1" max="18" placeholder="Hole" value={s.hole} onChange={e => updateSkin(i, 'hole', e.target.value)} />
                  <Input className="flex-1" placeholder="Winner name" value={s.name} onChange={e => updateSkin(i, 'name', e.target.value)} />
                  <Input className="w-20" type="number" placeholder="$" value={s.payout || ''} onChange={e => updateSkin(i, 'payout', e.target.value)} />
                  <button onClick={() => removeSkin(i)} className="text-red-500 hover:bg-red-50 p-1 rounded"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <Button onClick={addSkin} variant="outline" size="sm" className="w-full mt-2 text-xs" data-testid="add-skin-row">
              <Plus className="w-3 h-3 mr-1" /> Add skin
            </Button>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-bundle-green/70 mb-1.5">Notes (optional)</p>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Conditions, highlights, callouts..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !winner.name?.trim()}
            data-testid="save-results-btn"
            className="flex-1 bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold"
          >
            {saving ? 'Saving...' : 'Save Results'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
